// apps/web/src/features/crm/components/LinkedRequestsSection.tsx
//
// Shared presentational "Requests" section rendered inside the lead AND
// deal detail drawers (#1598). Shows the actor-scoped `count` plus the
// linked-request rows (code / status / SLA) from the #1596 reverse-read
// endpoints. A row click invokes `onOpenRequest(id)` so the host drawer
// can navigate to the China request workspace.
//
// INVARIANT (#1598): NON-PII only. The `rows` are `LinkedRequestSummary`
//   values (id/publicCode/status/slaDeadline) — never the full request
//   read model. This component must not render any name/phone/route field.
import { useTranslation } from 'react-i18next';
import type { LinkedRequestSummary } from '@magimp/shared';
import { RequestStatusPill } from '../../requests/display/index.js';
import { formatDateTime } from '../../../i18n/index.js';

export interface LinkedRequestsSectionProps {
  count: number;
  rows: readonly LinkedRequestSummary[];
  isLoading: boolean;
  isError: boolean;
  onOpenRequest: (requestId: string) => void;
}

export function LinkedRequestsSection({
  count,
  rows,
  isLoading,
  isError,
  onOpenRequest,
}: LinkedRequestsSectionProps) {
  const { t } = useTranslation(['crm', 'common']);
  return (
    <div data-testid="linked-requests-section">
      <div style={{ fontSize: 11, color: 'var(--fg-2)', marginBottom: 4 }}>
        {t('crm:requests.title')}
        {': '}
        <span data-testid="linked-requests-count">{count}</span>
      </div>
      {isLoading && (
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>{t('common:states.loading')}</div>
      )}
      {!isLoading && isError && (
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>{t('crm:requests.error')}</div>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--fg-2)' }} data-testid="linked-requests-empty">
          <em>{t('crm:requests.empty')}</em>
        </div>
      )}
      {!isLoading && !isError && rows.length > 0 && (
        <ul
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}
          data-testid="linked-requests-list"
        >
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => onOpenRequest(row.id)}
                data-testid="linked-request-row"
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  border: '1px solid var(--border-1)',
                  borderRadius: 'var(--r-1)',
                  background: 'var(--bg-1)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {row.publicCode}
                </span>
                <RequestStatusPill status={row.status} size="sm" />
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--fg-2)',
                    marginLeft: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                  aria-label={t('crm:requests.slaLabel')}
                >
                  {formatDateTime(row.slaDeadline)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
